import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useBlocker } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { articlesApi, isApiError } from '@shared/api';
import { articleDetailQuery, articleKeys } from '../api/queries';
import { articleOutcomeUnknown } from './presentation';

const monitorInterval = 2000;

/** Shared by the list and profile: one deletion intent retains its validator and replay key. */
export function useArticleDeletion({
  scope,
  id,
  canDelete,
}: {
  scope: string;
  id: string;
  canDelete: boolean;
}) {
  const client = useQueryClient();
  const { t } = useTranslation();
  const completionNotified = useRef(false);
  const [confirm, setConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>();
  const [uncertain, setUncertain] = useState(false);
  const [acceptedId, setAcceptedId] = useState<string>();
  const submission = useRef(false);
  const intent = useRef<{ etag: string; key: string } | null>(null);
  const detail = useQuery({
    ...articleDetailQuery(scope, id),
    refetchInterval: (query) =>
      query.state.data?.data.deletion?.state === 'pending' && !query.state.error
        ? monitorInterval
        : false,
  });
  const operationId = acceptedId ?? detail.data?.data.deletion?.operationId;
  const monitor = useQuery({
    queryKey: [...articleKeys.detail(scope, id), 'operation', operationId],
    queryFn: ({ signal }) => {
      if (!operationId) throw new Error('Article deletion operation is unavailable');
      return articlesApi.operation(operationId, signal);
    },
    enabled: Boolean(operationId),
    retry: false,
    refetchInterval: (query) =>
      query.state.error || (query.state.data && query.state.data.businessState !== 'accepted')
        ? false
        : monitorInterval,
  });
  const unresolvedDelete = uncertain && !operationId;
  const blocker = useBlocker({
    shouldBlockFn: () =>
      !operationId && Boolean(intent.current) && (submission.current || unresolvedDelete),
    enableBeforeUnload: !operationId && Boolean(intent.current) && (pending || unresolvedDelete),
    withResolver: true,
  });
  const deleted = monitor.data?.businessState === 'succeeded';
  const deletionState =
    monitor.data?.businessState === 'failed'
      ? 'failed'
      : (detail.data?.data.deletion?.state ?? (acceptedId ? 'pending' : undefined));
  const refresh = () => {
    void detail.refetch();
    if (operationId) void monitor.refetch();
  };
  const beginDelete = useCallback(async () => {
    if (submission.current || operationId || !canDelete) return;
    submission.current = true;
    setPending(true);
    setError(undefined);
    try {
      const current = await articlesApi.detail(id);
      client.setQueryData(articleKeys.detail(scope, id), current);
      if (current.data.deletion) return;
      intent.current = { etag: current.etag, key: crypto.randomUUID() };
      setConfirm(true);
    } catch (cause) {
      setError(cause);
    } finally {
      submission.current = false;
      setPending(false);
    }
  }, [canDelete, client, id, operationId, scope]);
  const remove = async () => {
    if (submission.current || !canDelete || !intent.current) return;
    submission.current = true;
    setPending(true);
    setError(undefined);
    try {
      const result = await articlesApi.delete(id, intent.current.etag, intent.current.key);
      setAcceptedId(result.data.operationId);
      setUncertain(false);
      setConfirm(false);
      void client.invalidateQueries({ queryKey: articleKeys.lists(scope) });
      void detail.refetch();
    } catch (cause) {
      setError(cause);
      setConfirm(false);
      const unknown = uncertain || articleOutcomeUnknown(cause);
      setUncertain(unknown);
      if (!unknown) {
        intent.current = null;
        refresh();
      }
    } finally {
      submission.current = false;
      setPending(false);
    }
  };
  useEffect(() => {
    if (!deleted || completionNotified.current) return;
    completionNotified.current = true;
    toast.success(t('articles.deletion.succeeded'));
    void client.invalidateQueries({ queryKey: articleKeys.lists(scope) });
  }, [client, deleted, scope, t]);
  const denied = isApiError(detail.error) && [401, 403].includes(detail.error.status);
  const closeConfirmation = (open: boolean) => {
    if (!pending) {
      setConfirm(open);
      if (!open) intent.current = null;
    }
  };
  return {
    detail,
    monitor,
    deleted,
    deletionState,
    unresolvedDelete,
    pending,
    error: operationId && uncertain ? undefined : error,
    confirm,
    blocker,
    denied,
    refresh,
    beginDelete,
    remove,
    closeConfirmation,
  };
}
